import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Bot, Send, RotateCcw, User as UserIcon, Loader2, AlertTriangle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const SUGESTOES = [
  { id: "pousadas", text: "Quais pousadas temos cadastradas?" },
  { id: "disponibilidade", text: "Quais quartos estão disponíveis?" },
  { id: "reservas-hoje", text: "Mostre as reservas de hoje." },
  { id: "orcamento", text: "Consulte um orçamento." },
];

function novaSessao() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch (err) { /* ignore */ }
  return "sess-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

function fmtTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export default function AgenteIA() {
  const [sessaoId, setSessaoId] = useState(() => novaSessao());
  const [mensagem, setMensagem] = useState("");
  const [historico, setHistorico] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);
  const scrollRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    try {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    } catch (err) { /* ignore */ }
  }, []);

  useEffect(() => { scrollToBottom(); }, [historico, enviando, scrollToBottom]);

  const carregarHistorico = useCallback(async (sid) => {
    try {
      const { data } = await api.get(`/ia/painel/sessoes/${sid}/historico`);
      setHistorico(Array.isArray(data?.itens) ? data.itens : []);
    } catch (err) {
      // Silencioso: sessão nova, backend desligado ou sem mensagens
      setHistorico([]);
    }
  }, []);

  useEffect(() => { carregarHistorico(sessaoId); }, [sessaoId, carregarHistorico]);

  const enviar = useCallback(async (texto) => {
    const payload = (texto ?? mensagem).trim();
    if (!payload || enviando) return;
    setErro(null);
    setEnviando(true);
    // optimistic UI
    const localUser = { papel: "user", conteudo: payload, criado_em: new Date().toISOString() };
    setHistorico((h) => [...h, localUser]);
    setMensagem("");
    try {
      const { data } = await api.post("/ia/painel/mensagem", { sessao_id: sessaoId, mensagem: payload });
      if (data?.ok) {
        // Recarrega o histórico oficial do backend (inclui tool calls persistidas)
        await carregarHistorico(sessaoId);
      } else {
        setErro(data?.erro || "Falha ao obter resposta.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "IA_PAINEL_DISABLED") {
        setErro("O Agente de IA está desativado no painel. Habilite IA_ENABLED_PAINEL para testar.");
      } else if (detail === "TURNS_EXCEEDED") {
        setErro("Limite diário de turnos atingido para o seu usuário.");
      } else if (detail === "COST_DAILY_EXCEEDED") {
        setErro("Limite diário de custo da IA foi atingido.");
      } else if (detail === "MENSAGEM_VAZIA") {
        setErro("A mensagem não pode estar vazia.");
      } else {
        setErro(detail || "Erro inesperado ao conversar com o agente.");
      }
    } finally {
      setEnviando(false);
    }
  }, [mensagem, enviando, sessaoId, carregarHistorico]);

  const novaConversa = useCallback(async () => {
    try { await api.post(`/ia/painel/sessoes/${sessaoId}/limpar`); } catch (err) { /* ignore */ }
    const nova = novaSessao();
    setSessaoId(nova);
    setHistorico([]);
    setMensagem("");
    setErro(null);
    toast.success("Nova conversa iniciada");
  }, [sessaoId]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  const mensagensVisiveis = useMemo(
    () => historico.filter((m) => m.papel === "user" || m.papel === "assistant"),
    [historico],
  );

  return (
    <div data-testid="agente-page" className="min-h-[calc(100vh-8rem)] flex flex-col">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-stone-500 mb-1">
            F-IA.3.2 · Preview Interno
          </div>
          <h1 className="font-serif text-2xl sm:text-3xl text-stone-900">Agente IA</h1>
          <p className="text-sm text-stone-600 mt-1">
            Assistente inteligente do Grupo Hospeda · uso administrativo
          </p>
        </div>
        <Button
          variant="outline"
          onClick={novaConversa}
          data-testid="agente-nova-conversa"
          className="shrink-0"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Nova conversa
        </Button>
      </div>

      <div className="flex-1 flex flex-col bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-hidden">
        <ScrollArea className="flex-1 px-4 py-6" ref={scrollRef}>
          {mensagensVisiveis.length === 0 && !enviando ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-stone-500 py-16">
              <div className="w-14 h-14 rounded-full bg-[#083A6B]/10 flex items-center justify-center mb-4">
                <Bot className="w-7 h-7 text-[#083A6B]" />
              </div>
              <h2 className="font-serif text-lg text-stone-800 mb-2">Como posso ajudar?</h2>
              <p className="text-sm text-stone-500 max-w-md mb-6">
                Faça consultas sobre pousadas, quartos, disponibilidade, hóspedes, reservas, pagamentos e orçamentos.
                Escritas só ocorrem após confirmação humana explícita.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-xl">
                {SUGESTOES.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`agente-sugestao-${s.id}`}
                    onClick={() => enviar(s.text)}
                    className="text-left text-sm px-4 py-3 rounded-md border border-stone-200 bg-white hover:border-[#083A6B]/40 hover:bg-stone-50 transition"
                  >
                    {s.text}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {mensagensVisiveis.map((m, idx) => {
                const isUser = m.papel === "user";
                return (
                  <div
                    key={idx}
                    data-testid="agente-mensagem"
                    data-papel={m.papel}
                    className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`}
                  >
                    {!isUser && (
                      <Avatar className="w-8 h-8 shrink-0">
                        <AvatarFallback className="bg-[#083A6B] text-white">
                          <Bot className="w-4 h-4" />
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <div
                      className={[
                        "max-w-[80%] rounded-lg px-4 py-2.5 text-sm leading-relaxed",
                        isUser
                          ? "bg-[#083A6B] text-white"
                          : "bg-white border border-stone-200 text-stone-800",
                      ].join(" ")}
                    >
                      {isUser ? (
                        <div className="whitespace-pre-wrap break-words">{m.conteudo}</div>
                      ) : (
                        <div
                          data-testid="agente-mensagem-md"
                          className="markdown-ia break-words [overflow-wrap:anywhere]"
                        >
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              p: (props) => <p className="my-2 leading-relaxed" {...props} />,
                              h1: (props) => <h3 className="font-bold text-base mt-3 mb-1.5" {...props} />,
                              h2: (props) => <h2 className="font-bold text-base mt-3 mb-1.5" {...props} />,
                              h3: (props) => <h3 className="font-bold text-sm mt-3 mb-1.5" {...props} />,
                              h4: (props) => <h4 className="font-semibold text-sm mt-3 mb-1" {...props} />,
                              strong: (props) => <strong className="font-semibold" {...props} />,
                              em: (props) => <em className="italic" {...props} />,
                              ul: (props) => <ul className="my-2 pl-5 list-disc space-y-1" {...props} />,
                              ol: (props) => <ol className="my-2 pl-5 list-decimal space-y-1" {...props} />,
                              li: (props) => <li className="leading-relaxed" {...props} />,
                              a: (props) => <a className="text-[#083A6B] underline" target="_blank" rel="noopener noreferrer" {...props} />,
                              code: ({ inline, className, children, ...props }) => (
                                inline ? (
                                  <code className="bg-stone-100 text-stone-800 rounded px-1 py-0.5 text-xs" {...props}>{children}</code>
                                ) : (
                                  <code className="block bg-stone-100 text-stone-800 rounded p-2 text-xs overflow-x-auto whitespace-pre" {...props}>{children}</code>
                                )
                              ),
                              table: (props) => <div className="overflow-x-auto my-2"><table className="min-w-full text-xs border border-stone-200" {...props} /></div>,
                              th: (props) => <th className="border border-stone-200 px-2 py-1 bg-stone-50 text-left font-semibold" {...props} />,
                              td: (props) => <td className="border border-stone-200 px-2 py-1" {...props} />,
                              blockquote: (props) => <blockquote className="border-l-2 border-stone-300 pl-3 my-2 text-stone-700 italic" {...props} />,
                              hr: () => <hr className="my-3 border-stone-200" />,
                            }}
                          >
                            {String(m.conteudo || "")}
                          </ReactMarkdown>
                        </div>
                      )}
                      <div className={`text-[10px] mt-1 ${isUser ? "text-white/60" : "text-stone-400"}`}>
                        {fmtTime(m.criado_em)}
                      </div>
                    </div>
                    {isUser && (
                      <Avatar className="w-8 h-8 shrink-0">
                        <AvatarFallback className="bg-stone-200 text-stone-700">
                          <UserIcon className="w-4 h-4" />
                        </AvatarFallback>
                      </Avatar>
                    )}
                  </div>
                );
              })}
              {enviando && (
                <div
                  data-testid="agente-loading"
                  className="flex gap-3 justify-start items-center text-sm text-stone-500"
                >
                  <Avatar className="w-8 h-8 shrink-0">
                    <AvatarFallback className="bg-[#083A6B] text-white">
                      <Bot className="w-4 h-4" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="bg-white border border-stone-200 rounded-lg px-4 py-2.5 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Pensando…</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        {erro && (
          <div
            data-testid="agente-erro"
            className="px-4 py-3 border-t border-amber-200 bg-amber-50 text-amber-900 text-sm flex gap-2 items-start"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>{erro}</div>
          </div>
        )}

        <div className="border-t border-stone-200 bg-white px-4 py-3">
          <div className="flex gap-2 items-end">
            <Textarea
              data-testid="agente-input"
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Pergunte algo ao agente…"
              rows={2}
              maxLength={2000}
              className="resize-none"
              disabled={enviando}
            />
            <Button
              data-testid="agente-enviar"
              onClick={() => enviar()}
              disabled={enviando || !mensagem.trim()}
              className="shrink-0 bg-[#083A6B] hover:bg-[#243d31] text-white"
            >
              {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              <span className="ml-2 hidden sm:inline">Enviar</span>
            </Button>
          </div>
          <div className="text-[11px] text-stone-400 mt-2">
            Enter para enviar · Shift+Enter para nova linha · Sessão {sessaoId.slice(0, 8)}…
          </div>
        </div>
      </div>
    </div>
  );
}
